import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { FaClock, FaExclamationCircle, FaMoneyBillWave, FaRedo } from "react-icons/fa";

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;

/**
 * Where JazzCash / Easypaisa send the customer when a payment did not go
 * through. The order is kept for a while, so the customer can try again or
 * switch to cash on delivery. A late payment is picked up on "Check again".
 */
export default function PaymentResult() {
  const { orderId } = useParams();
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const tracking = query.get("t") || "";
  const result = query.get("result") || "failed";
  const gatewayMessage = query.get("message") || "";

  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const goToSuccess = useCallback(() => {
    history.replace(`/order-success/${orderId}?paid=1&t=${encodeURIComponent(tracking)}`);
  }, [history, orderId, tracking]);

  const load = useCallback((check = false) => {
    setBusy(check ? "check" : "");
    return axios.get(`/api/payments/${orderId}/status`, { params: { t: tracking, check: check ? 1 : 0 } })
      .then((res) => {
        if (res.data.status !== 200) {
          setError("We could not find this order. Use Track order with your tracking number.");
          return;
        }
        if (res.data.state === "paid") {
          goToSuccess();
          return;
        }
        setInfo(res.data);
      })
      .catch(() => setError("We could not reach the store. Please check your connection."))
      .finally(() => setBusy(""));
  }, [orderId, tracking, goToSuccess]);

  useEffect(() => {
    document.title = "Payment";
    // A pending payment may finish a moment later: ask the gateway once.
    load(result === "pending");
  }, [load, result]);

  const switchToCod = () => {
    setBusy("cod");
    setError("");
    axios.post(`/api/payments/${orderId}/switch-to-cod`, { t: tracking })
      .then((res) => {
        if (res.data.status === 200) {
          try {
            const saved = JSON.parse(sessionStorage.getItem("last_order_details") || "null");
            if (saved) sessionStorage.setItem("last_order_details", JSON.stringify({ ...saved, paymentMethod: "Cash on Delivery (COD)" }));
          } catch {
            // ignore
          }
          history.replace(`/order-success/${orderId}`);
        } else {
          setError(res.data.message || "This order could not be changed.");
        }
      })
      .catch(() => setError("This order could not be changed. Please try again."))
      .finally(() => setBusy(""));
  };

  const payUrl = `${axios.defaults.baseURL || ""}/api/payments/${orderId}/start?t=${encodeURIComponent(tracking)}`;
  const minutesLeft = info?.due_at ? Math.max(0, Math.round((new Date(info.due_at.replace(" ", "T")) - Date.now()) / 60000)) : null;
  const canRetry = info && info.state === "unpaid" && (minutesLeft === null || minutesLeft > 0);

  let title = "Your payment did not go through";
  let text = `No money was taken. Your order is saved${minutesLeft ? ` for ${minutesLeft} more minute${minutesLeft === 1 ? "" : "s"}` : ""}. Try again, or pay cash when it arrives.`;
  if (result === "pending") {
    title = "Your payment is still processing";
    text = `${info?.gateway || "The payment company"} has not confirmed it yet. If you paid, press Check again in a minute. You will not be charged twice.`;
  }
  if (info?.state === "cancelled") {
    title = "This order was cancelled";
    text = "The payment was not completed in time, so the items went back on sale. You can place the order again.";
  }
  if (info?.state === "cod") {
    title = "This order is cash on delivery";
    text = "No online payment is needed. Pay when the order arrives.";
  }

  return (
    <div className="py-5" style={{ backgroundColor: "#f8fafc", minHeight: "70vh" }}>
      <div className="container" style={{ maxWidth: "560px" }}>
        <div className="bg-white p-4 p-md-5 rounded-4 border text-center">
          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: 64, height: 64, background: result === "pending" ? "#fef3c7" : "#fee2e2", color: result === "pending" ? "#b45309" : "#b91c1c" }}
          >
            {result === "pending" ? <FaClock size={28} /> : <FaExclamationCircle size={28} />}
          </div>
          <h3 className="fw-bold mb-2">{title}</h3>
          <p className="text-muted mb-3">{text}</p>
          {gatewayMessage && result !== "pending" && <p className="small text-muted mb-3">Message: {gatewayMessage}</p>}
          {info && (
            <p className="mb-4">
              Order <strong>#{orderId}</strong> · {money(info.total)}{info.gateway ? ` · ${info.gateway}` : ""}
            </p>
          )}
          {error && <div className="alert alert-warning small">{error}</div>}

          <div className="d-flex flex-column gap-2">
            {canRetry && (
              <a href={payUrl} className="btn btn-dark btn-lg rounded-pill fw-bold d-flex align-items-center justify-content-center gap-2">
                <FaRedo size={14} /> Try {info.gateway || "payment"} again
              </a>
            )}
            {info?.state === "unpaid" && (
              <button type="button" className="btn btn-outline-secondary rounded-pill fw-semibold" onClick={() => load(true)} disabled={!!busy}>
                {busy === "check" ? "Checking…" : "I paid. Check again"}
              </button>
            )}
            {info?.state === "unpaid" && info.cod_available && (
              <button type="button" className="btn btn-outline-dark rounded-pill fw-semibold d-flex align-items-center justify-content-center gap-2" onClick={switchToCod} disabled={!!busy}>
                <FaMoneyBillWave size={14} /> {busy === "cod" ? "Switching…" : "Pay cash on delivery instead"}
              </button>
            )}
            <Link to="/collection" className="btn btn-link text-muted">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
